"use client";

// src/ui/components/calculator/cockpit.tsx
//
// The result-first "lab instrument" cockpit for the Peptidrechner. Renders the
// live draw-up number, syringe, stat tiles, and (for blends) the per-injection
// component breakdown. Consumes calculator state + result via props — owns no
// state of its own except the controlled display-unit segmented control.

import { useId, useMemo, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { SyringeSvg } from "@/ui/components/calculator/syringe-svg";
import { componentBreakdown, formatDosesPerVialDisplay } from "@/lib/calculator/calculations";
import { getPeptideCalculatorMode, getPeptideCategory } from "@/lib/calculator/peptide-data";
import { isFiveProductStudyContextSlug } from "@/config/five-product-launch";
import type { CalculatorState } from "@/lib/calculator/use-calculator-state";
import type { CalculationResult, DisplayUnit, PeptideData } from "@/lib/calculator/types";
import type { Locale } from "@/config/locale";

/**
 * Per-peptide accent for ONLY the big cockpit number + syringe strike.
 * GLP-1/GIP → primary (navy), repair (BPC/TB/AOD/GHK) → success (green),
 * blends → warning (amber). Everything else stays primary.
 */
export function accentClassFor(peptide: PeptideData, locale: Locale): string {
	if (peptide.components?.length) return "text-warning";
	const category = getPeptideCategory(peptide, locale);
	if (/GLP-1|GIP/i.test(category)) return "text-primary";
	if (/Stack/i.test(category)) return "text-warning";
	if (/^(BPC|TB|AOD|GHK)/i.test(peptide.slug) || /Regenerat|Repair|Heilung|Haut|Kosmet/i.test(category)) {
		return "text-success";
	}
	return "text-primary";
}

interface SegmentedProps {
	value: DisplayUnit;
	onChange: (value: DisplayUnit) => void;
}

function UnitSegmented({ value, onChange }: SegmentedProps) {
	const t = useTranslations("peptideCalculator");
	const groupName = useId();
	const unitItems: { value: DisplayUnit; label: string }[] = [
		{ value: "iu", label: "IU" },
		{ value: "ml", label: "ml" },
		{ value: "marks", label: t("cockpit.marks") },
	];
	return (
		<div
			role="radiogroup"
			aria-label={t("cockpit.unitAriaLabel")}
			className="bg-muted/50 inline-flex shrink-0 items-center rounded-full border border-border p-0.5"
		>
			{unitItems.map((item) => {
				const active = value === item.value;
				return (
					<label
						key={item.value}
						className={`relative inline-flex min-h-11 cursor-pointer items-center whitespace-nowrap rounded-full px-2 text-xs font-semibold transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 sm:px-2.5 ${
							active
								? "bg-primary text-primary-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground"
						}`}
					>
						<input
							type="radio"
							name={groupName}
							value={item.value}
							checked={active}
							onChange={() => onChange(item.value)}
							className="sr-only"
						/>
						{item.label}
					</label>
				);
			})}
		</div>
	);
}

interface StatTileProps {
	label: string;
	value: string;
	unit?: string;
	green?: boolean;
}

function StatTile({ label, value, unit, green }: StatTileProps) {
	return (
		<div
			className={`min-w-0 rounded-[11px] border p-2.5 ${
				green ? "border-success/30 bg-success/10" : "bg-muted/30 border-border"
			}`}
		>
			<div className="truncate text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
				{label}
			</div>
			<div
				className={`mt-1 font-mono text-lg font-bold tabular-nums leading-none tracking-[-0.02em] transition-colors duration-150 ${
					green ? "text-success" : "text-foreground"
				}`}
			>
				{value}
			</div>
			{unit && <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">{unit}</div>}
		</div>
	);
}

interface CockpitProps {
	state: CalculatorState;
	result: CalculationResult;
	peptide: PeptideData;
	locale: string;
	displayUnit: DisplayUnit;
	onDisplayUnitChange: (value: DisplayUnit) => void;
	/** Formatted "pro Dosis" label (e.g. "12,40 €"); omit/undefined to hide the value. */
	pricePerDoseLabel?: string;
	productLink?: ReactNode;
}

export function Cockpit({
	state,
	result,
	peptide,
	locale,
	displayUnit,
	onDisplayUnitChange,
	pricePerDoseLabel,
	productLink,
}: CockpitProps) {
	const t = useTranslations("peptideCalculator");
	const loc = locale as Locale;

	const hasResult = result.volume > 0;
	const accentClass = accentClassFor(peptide, loc);
	const isResearchDilution = getPeptideCalculatorMode(peptide) === "research-dilution";
	const hasNeutralResearchUi = isResearchDilution || isFiveProductStudyContextSlug(peptide.slug);

	const iu = Math.round(result.insulinUnits);
	// Syringe scale follows the SELECTED syringe AND the chosen calibration. The big
	// number (result.insulinUnits = volume × calibration) is already calibration-aware,
	// so the denominator must match: a 1.0 ml barrel reads 100 IU at U-100 but 40 IU at
	// U-40. Hardwiring ×100 here made the big number and the syringe graphic disagree.
	const totalIU = Math.round(state.syringeSize * state.calibration);

	// Big number per selected display unit. ml shows 3 decimals; IU/Striche show rounded IU.
	const effectiveDisplayUnit = hasNeutralResearchUi ? "ml" : displayUnit;
	const bigValue = !hasResult ? "—" : effectiveDisplayUnit === "ml" ? result.volume.toFixed(3) : String(iu);
	const bigUnit =
		effectiveDisplayUnit === "ml" ? "ml" : effectiveDisplayUnit === "marks" ? t("cockpit.marks") : "IU";

	const blend = useMemo(
		() => componentBreakdown(peptide.components, state.dose),
		[peptide.components, state.dose],
	);

	return (
		<div className="border-primary/60 flex flex-col rounded-[18px] border bg-card p-4 shadow-md sm:p-5">
			{/* Header: eyebrow + live dot · unit segmented control. The eyebrow is allowed to
			    shrink + truncate (min-w-0) so the unit segmented control always fits on one
			    row down to 360px without clipping past the card edge; mobile tightens the
			    tracking so "IHR ERGEBNIS · live" stays whole next to IU/ml/Striche. */}
			<div className="flex items-center justify-between gap-2">
				{/* Eyebrow (not a heading): keeping a <span> avoids a browser-extension/dev
				    hot-reload hydration mismatch some users hit on an <h2> here. The result
				    value below carries aria-live, so the region stays screen-reader friendly. */}
				<span className="inline-flex min-w-0 items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-primary sm:tracking-[0.13em]">
					<span
						aria-hidden
						className="h-2 w-2 shrink-0 rounded-full bg-success motion-safe:animate-[pr-pulse_2s_infinite]"
					/>
					<span className="truncate">
						{t("cockpit.eyebrow")} · {t("cockpit.live")}
					</span>
				</span>
				{!hasNeutralResearchUi && <UnitSegmented value={displayUnit} onChange={onDisplayUnitChange} />}
			</div>

			{/* Big draw-up number */}
			<div className="mt-5 text-center sm:mt-6">
				<div className="text-[11px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">
					{hasNeutralResearchUi ? t("researchDilution.measurementVolume") : t("cockpit.drawUp")}
				</div>
				<div
					className={`mt-1.5 font-mono font-bold tabular-nums leading-none ${accentClass}`}
					aria-live="polite"
				>
					<span className="text-[54px] leading-none tracking-[-0.03em]">{bigValue}</span>
					<span className="ml-1.5 text-xl font-semibold text-muted-foreground">{bigUnit}</span>
				</div>
			</div>

			{/* The syringe visual is intentionally absent from neutral research-dilution calculations. */}
			{!hasNeutralResearchUi && (
				<div className="mt-5 sm:mt-6">
					<SyringeSvg
						syringeSize={state.syringeSize}
						fillMl={result.volume}
						calibration={state.calibration}
						accentClassName={accentClass}
						className="w-full"
					/>
					<div className="mt-1 text-center font-mono text-xs tabular-nums text-muted-foreground">
						{hasResult ? `${iu} IU / ${totalIU} IU (${result.volume.toFixed(3)} ml)` : `— / ${totalIU} IU`}
					</div>
				</div>
			)}

			{/* 4 stat tiles — 2-col on small mobile (≤360px stays readable, no clipping),
			    4-col from sm up per the handoff desktop spec. */}
			<div
				className={`mt-5 grid grid-cols-2 gap-2 sm:mt-6 sm:gap-2.5 ${hasNeutralResearchUi ? "sm:grid-cols-3" : "sm:grid-cols-4"}`}
			>
				<StatTile
					label={t("result.concentration")}
					value={result.concentration > 0 ? result.concentration.toFixed(2) : "—"}
					unit="mg/ml"
				/>
				<StatTile
					label={hasNeutralResearchUi ? t("researchDilution.sampleVolume") : t("result.volume")}
					value={result.volume > 0 ? result.volume.toFixed(3) : "—"}
					unit="ml"
				/>
				<StatTile
					label={hasNeutralResearchUi ? t("researchDilution.samplesPerVial") : t("result.dosesPerVial")}
					value={formatDosesPerVialDisplay(result.dosesPerVial)}
					unit={hasNeutralResearchUi ? t("researchDilution.samplesUnit") : t("cockpit.dosesUnit")}
				/>
				{!hasNeutralResearchUi && (
					<StatTile label={t("cockpit.perDose")} value={pricePerDoseLabel ?? "—"} green />
				)}
			</div>

			{/* Blend breakdown — only for documented multi-component vials */}
			{blend && (
				<div className="bg-muted/40 mt-4 rounded-xl border border-border p-3">
					<div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
						{t("cockpit.blendTitle")}
					</div>
					<div className="mt-2 space-y-1.5">
						{blend.map((c) => (
							<div key={c.name} className="flex items-center gap-2 text-sm">
								<span
									aria-hidden
									className={`h-2 w-2 shrink-0 rounded-full ${c.anchor ? "bg-warning" : "bg-muted-foreground/50"}`}
								/>
								<span className="min-w-0 flex-1 truncate text-foreground">
									{c.name}
									{c.anchor && (
										<span className="bg-warning/15 ml-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning">
											{t("cockpit.blendAnchor")}
										</span>
									)}
								</span>
								<span className="shrink-0 font-mono text-sm font-semibold tabular-nums text-foreground">
									{c.v} {c.u}
								</span>
							</div>
						))}
					</div>
				</div>
			)}
			{productLink}
		</div>
	);
}
